import { Box, DialogContent, Grid, Stack, TextField, Typography } from '@mui/material'
import Dialog from '@mui/material/Dialog'
import { CarrierVisitStatus, CompanyType, OrderResponseDto } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { InitializationWrapper } from '@planning/components'
import { CompanyAutocomplete } from '@planning/components/autocompletes/company-autocomplete/CompanyAutocomplete'
import { DriverAutoComplete } from '@planning/components/autocompletes/driver-autocomplete/DriverAutoComplete'
import { CustomDatePicker } from '@planning/components/CustomDatePicker'
import { CustomTimePicker } from '@planning/components/CustomTimePicker'
import { CustomAlert, useGateControlMessageHandler } from '@planning/index'
import { ExternalDriverFormDialog } from '@planning/pages/ExternalDriver/Components/Organisms/ExternalDriverFormDialog'
import { generateEirDataForOrder, IGateInFormData } from '@planning/pages/GateClerk'
import { IInspectContainerFormData } from '@planning/pages/GateClerk/Components/InspectContainer'
import { OrderHandlingBox } from '@planning/pages/GateClerk/Components/OrderHandlingBox'
import { RoutingSlip, RoutingSlipItem } from '@planning/pages/GateClerk/Components/RoutingSlip'
import { NoOrdersHint } from '@planning/pages/GateClerk/NoOrdersHint'
import { isoCodeMappingFieldRenderFnType } from '@planning/pages/Order/stores/CreateOrderPageViewStore'
import { orderService, truckVisitService } from '@planning/services'
import { useNavigationStore } from '@planning/stores'
import { GetContainersRoutingInfoFunc } from '@planning/stores/gateClerk/ContainerBlockInfoDto'
import { GetEasiestContainerToReachFunc } from '@planning/stores/gateClerk/SuggestedContainerDto'
import { GateInViewStore } from '@planning/stores/gateControl/GateInViewStore'
import { PrintoutsViewStore } from '@planning/stores/printouts/PrintoutViewStore'
import { useTranslate } from '@tolgee/react'
import {
  ConfirmationDialog,
  EquipmentInterchangeReceipt,
  PdfFileViewer,
  useMinimalsTheme,
} from '@tom-ui/ui'
import { escapeTruckNumber, PATHS, useAsyncFetch } from '@tom-ui/utils'
import dayjs from 'dayjs'
import _ from 'lodash'
import { observer } from 'mobx-react-lite'
import moment from 'moment'
import React, { FC, useEffect, useMemo } from 'react'
import { Controller, useForm, UseFormReset } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router'
import { DropOffAutoComplete } from './DropOffAutoComplete'
import { InspectContainer } from './InspectContainer'
import { TruckAppointmentPageHeader } from './Organisms/TruckAppointment/TruckAppointmentPageHeader'
import { PickUpAutoComplete } from './PickUpAutoComplete'
import { SelectPickUpAmountAlert } from './SelectPickUpAmountAlert'

interface IProps {
  store: GateInViewStore
}

interface IDialogProps extends IProps {
  reset: UseFormReset<IGateInFormData>
}

interface IGateInDialogProps extends IProps, IGateInFormDialogProps {}

const InspectContainerWrapper: FC<IProps> = ({ store }) => {
  return (
    store.editedOrder && (
      <InspectContainer
        order={store.editedOrder}
        damages={store.editedOrder.damages}
        displayDamageReport={store.editedType === 'dropOff'}
        onSubmit={(data: IInspectContainerFormData) => {
          if (store.editedType === 'pickUp') {
            store.upsertPickUpOrder(data)
          } else {
            store.upsertDropOffOrder(data)
          }
          store.resetEditMode()
        }}
        onCancel={() => store.resetEditMode()}
      />
    )
  )
}

export const GateInDialog = observer(({ store, ...formProps }: IGateInDialogProps) => {
  const RenderDialogMode = () => {
    if (store.isDialogEditMode && store.editedOrder?.id) {
      return <InspectContainerWrapper store={store}></InspectContainerWrapper>
    } else {
      return <GateInFormDialog {...formProps} />
    }
  }

  return (
    <Dialog fullWidth maxWidth={'lg'} open={store.isGateInDialogOpen}>
      {RenderDialogMode()}
    </Dialog>
  )
})

const GateInFormDialogs: FC<IDialogProps> = observer(({ store, reset }: IDialogProps) => {
  const { t } = useTranslate()
  const navigationStore = useNavigationStore()
  const { appViewStore } = usePlanningStore()

  const onClose = () => {
    store.reset()
    reset()
    navigationStore.pop()
  }

  const deleteOrder = () => {
    if (!store.selectedOrder) return

    const { id } = store.selectedOrder

    if (store.deleteFrom === 'DropOff') {
      store.deleteDropOffOrder(id)
      store.deleteBooking(id)
    } else store.deletePickUpOrder(id)

    store.resetDialogs()
  }

  const deleteNNROrder = () => {
    if (!store.selectedNNROrder) return

    store.deleteNNROrder(store.selectedNNROrder.id)
    store.resetDialogs()
  }

  const onConfirmRemoval = () => {
    store.selectedOrder ? deleteOrder() : deleteNNROrder()
    appViewStore.setShowAlert(
      'success',
      t(
        'containerWasSuccessfullyRemovedFromTheTruckVisit',
        'Container was successfully removed from the truck visit',
      ),
    )
  }

  return (
    <>
      <ConfirmationDialog
        open={store.isRemoveDialogOpen}
        title={`${t('areYouSureYouWantToRemove', 'Are you sure you want to remove')} ${
          store.selectedOrder
            ? store.selectedOrder.containerNumber
            : store.selectedNNROrder?.referenceNumber
        }?`}
        primaryActionText={t('yesRemove', 'Yes, remove')}
        closeLabel={t('noCancel', 'No, cancel')}
        onConfirm={onConfirmRemoval}
        onClose={store.resetDialogs}
      />

      <ConfirmationDialog
        open={store.isCancelGateDialogOpen}
        title={t('cancelGateIn?', 'Cancel gate in?')}
        message={t('anyChangesYouMadeWillBeLost', 'Any changes you made will be lost')}
        closeLabel={t('noDontCancel', `No, don't cancel`)}
        primaryActionText={t('cancel', 'Cancel')}
        onClose={store.resetDialogs}
        onConfirm={onClose}
        mobile
      />

      <Dialog fullWidth maxWidth={'lg'} open={store.isDialogEditMode && !!store.editedOrder?.id}>
        <InspectContainerWrapper store={store}></InspectContainerWrapper>
      </Dialog>
    </>
  )
})

export interface IGateInFormDialogProps {
  validateAllocationSpace?: (inboundOrderIds: number[]) => Promise<number[]>
  getEasiestContainerToReachFunc?: GetEasiestContainerToReachFunc
  getJourneyPositions?: GetContainersRoutingInfoFunc
  getGeneralCargoRoutingInfo?: GetGeneralCargoRoutingInfoFunc
  renderRouteMiniMap?: (visitId?: number) => Promise<React.ReactElement | null>
  isoCodeMappingFieldRenderFn?: isoCodeMappingFieldRenderFnType
}

export interface GeneralCargoRoutingInfo {
  orderId: number
  location: string
}

export type GetGeneralCargoRoutingInfoFunc = (
  orders: IInspectContainerFormData[],
) => Promise<GeneralCargoRoutingInfo[]>

export const GateInFormDialog: FC<IGateInFormDialogProps> = observer(
  ({
    validateAllocationSpace,
    getEasiestContainerToReachFunc,
    getJourneyPositions,
    getGeneralCargoRoutingInfo,
    renderRouteMiniMap,
    isoCodeMappingFieldRenderFn,
  }) => {
    const params = useParams()
    const navigate = useNavigate()
    const theme = useMinimalsTheme()

    const { t } = useTranslate()

    const {
      appViewStore,
      dialogStore,
      tenantStore,
      truckVisitAggregationStore,
      gateInViewStore: store,
      orderItemStore,
      vesselVisitItemStore,
    } = usePlanningStore()
    const { notificationStore: gateInNotificationStore } = store
    const navigationStore = useNavigationStore()

    const { getExceptionMessage } = useGateControlMessageHandler()

    store.validateAllocationSpace = validateAllocationSpace
    store.dropOffOrderSearchStore.validateAllocationSpace = validateAllocationSpace
    store.isoCodeMappingFieldRenderFn = isoCodeMappingFieldRenderFn
    gateInNotificationStore.getEasiestContainerToReachFunc = getEasiestContainerToReachFunc
    gateInNotificationStore.upsertNnOrders = store.upsertNNROrder

    const printoutsViewStore = useMemo(() => new PrintoutsViewStore(), [])

    const id = params.id ? parseInt(params.id) : undefined

    const { loading } = useAsyncFetch(async () => {
      if (id) {
        const truckVisit = await truckVisitService.getWithOrdersById(id)

        if (truckVisit.status !== CarrierVisitStatus.Expected) {
          store.reset()
          navigate(PATHS.visits.truckVisits.url, { replace: true })
          return
        }

        if (truckVisit) {
          truckVisitAggregationStore.upsertVisitsToItemStores([truckVisit])

          const truckVisitItem = truckVisitAggregationStore.truckVisits.find(t => t.id === id)
          if (truckVisitItem) {
            store.openEditTruckVisitAppointment(truckVisitItem)
            store.setExternalDriver({
              ...truckVisit.driver,
              id: truckVisit.driver?.id ?? 0,
              driverID: truckVisit.driver?.driverID ?? '',
              name: truckVisit.driver?.name ?? '',
              banned: truckVisit.driver?.banned ?? false,
            })
            store.setTruckCompany({
              ...truckVisit.truckCompany,
              id: truckVisit.truckCompany?.id ?? 0,
              name: truckVisit.truckCompany?.name ?? '',
              shortName: truckVisit.truckCompany?.shortName ?? '',
              referenceId: 0,
            })
            store.setExternalPortGatePassRef(truckVisit.externalPortGatePassRef ?? '')
          }
        }
      }
    }, [id, navigate, store, truckVisitAggregationStore])

    useEffect(() => {
      store.setIsTruckAppointment(!tenantStore.skipTruckAppointment)

      return () => store.reset()
    }, [store, tenantStore.skipTruckAppointment])

    const { isTruckAppointment, pickUpGeneralCargoOrders, dropOffGeneralCargoOrders } = store

    const {
      register,
      control,
      handleSubmit,
      reset,
      formState: { errors },
    } = useForm<IGateInFormData>({
      values: {
        truckPlate: store.licensePlate,
        trailerPlate: store.trailerPlate,
        driverName: store.externalDriver?.name ?? store.driverName,
        driverID: store.externalDriver?.driverID,
        dropOffOrders: store.dropOffOrders,
        pickUpOrders: store.pickUpOrders,
        nnrOrders: store.nnrOrders,
        eta: store.truckAppointmentEstimatedTimes.eta,
        etd: store.truckAppointmentEstimatedTimes.etd,
        truckAppointmentDate: store.truckAppointmentDate,
        externalPortGatePassRef: store.externalPortGatePassRef,
        externalDriverId: store.externalDriver?.id,
        truckCompanyId: store.truckCompany?.id,
        generalCargoOrders: [],
        bookings: [],
      },
    })

    const validateIfShouldCreateOverlappingVisit = async (
      licensePlate: string,
      from: string | null,
      to: string | null,
      truckVisitId?: number,
    ) => {
      if (from && to) {
        store.setLoading(true)

        const overlapping = await truckVisitService.getOverlappingVisit(
          licensePlate,
          from,
          to,
          truckVisitId,
        )

        store.setLoading(false)

        if (overlapping) {
          const confirmCreation = await appViewStore.setOpenConfirmDialog(
            true,
            <Stack>
              <Typography variant='body1' color='secondary'>
                {t(
                  'truckVisitWithSameLicensePlateAndOverlappingEstimatedTimes',
                  'A truck visit with the same license plate and overlapping estimated times already exists.',
                )}
              </Typography>
              <Typography variant='body1' color='secondary'>
                {t('doYouWantToContinue', 'do you want to continue?')}
              </Typography>
            </Stack>,
            t('overlappingTruckVisit', 'Overlapping truck visit'),
            undefined,
            {
              primaryActionText: t('yesContinue', 'Yes, continue'),
            },
          )

          return confirmCreation
        }
      }

      return true
    }

    const onGateInFormSubmit = async (data: IGateInFormData) => {
      const {
        truckVisitId,
        dropOffOrders,
        pickUpOrders,
        bookings,
        nnrOrders,
        pickUpGeneralCargoOrders,
        dropOffGeneralCargoOrders,
        isTruckAppointment,
        truckAppointmentEstimatedTimes,
      } = store
      const generalCargoOrders = pickUpGeneralCargoOrders.concat(dropOffGeneralCargoOrders)
      const allowGateIn = !isTruckAppointment

      const requestData: IGateInFormData = {
        ...data,
        externalDriverId: store.externalDriver?.id,
        truckCompanyId: store.truckCompany?.id,
        truckVisitId,
        dropOffOrders,
        pickUpOrders,
        bookings,
        nnrOrders,
        generalCargoOrders,
        eta: truckAppointmentEstimatedTimes.eta
          ? moment(truckAppointmentEstimatedTimes.eta).toISOString()
          : null,
        etd: truckAppointmentEstimatedTimes.etd
          ? moment(truckAppointmentEstimatedTimes.etd).toISOString()
          : null,
      }

      const numericReleaseTransportCompanyIds = requestData.pickUpOrders
        .map(o => o.numericReleaseTransportCompanyId)
        .filter((value, index, self) => value !== undefined && self.indexOf(value) === index)

      const nonNumericReleaseTransportCompanyIds = requestData.nnrOrders
        .map(o => o.transportCompanyId)
        .filter((value, index, self) => value !== undefined && self.indexOf(value) === index)

      const companyIds = [
        ...numericReleaseTransportCompanyIds,
        ...nonNumericReleaseTransportCompanyIds,
      ]

      const containsNonMatchingCompanyId = companyIds.some(
        companyId => companyId && companyId !== requestData?.truckCompanyId,
      )

      if (containsNonMatchingCompanyId) {
        await appViewStore.setOpenConfirmDialog(
          true,
          t(
            'truckCompanyMismatchDetailed',
            'The transport company in the truck visit does not match the one on the order release.',
          ),
          t('inconsistentTransportInformation', 'Inconsistent transport information?'),
          undefined,
          {
            maxWidth: 'sm',
            hideSecondaryAction: true,
            primaryActionText: t('Ok'),
          },
        )

        return
      }

      try {
        const confirmCreation = await validateIfShouldCreateOverlappingVisit(
          requestData.truckPlate,
          requestData.eta,
          requestData.etd,
          requestData.truckVisitId,
        )
        if (!confirmCreation) return

        store.setLoading(true)

        const appointment = await gateInNotificationStore.submitGateInData(
          requestData,
          allowGateIn,
        )
        // For brand-new gate-ins the form had no truckVisitId; pull it from the
        // server response so the routing-slip mini-map can fetch the route.
        if (appointment?.truckVisitId) requestData.truckVisitId = appointment.truckVisitId
        appViewStore.setShowAlert(
          'success',
          t(
            'changesToTheTruckVisitAreSuccessfullySaved',
            'Changes to the truck visit are successfully saved',
          ),
        )
        if (allowGateIn) await generateDocuments(requestData)

        store.reset()
        reset()
        navigationStore.clear()

        navigate(PATHS.visits.truckVisits.url)
      } catch (error: any) {
        store.setIsTruckAppointment(!tenantStore.skipTruckAppointment)
        appViewStore.setShowAlert('error', getExceptionMessage(error))
      } finally {
        store.setLoading(false)
      }
    }

    async function generateDocuments(data: IGateInFormData) {
      try {
        printoutsViewStore.setLoading(true)
        const now = new Date()

        await generateRoutingSlip(data, now)
        await generateDropoffEir(data, now)
        await printoutsViewStore.uploadDocuments()

        if (printoutsViewStore.documents.length > 0) {
          dialogStore.openDialog(
            <PdfFileViewer
              onClose={() => dialogStore.close()}
              files={printoutsViewStore.pdfFileViewerDocuments}
            />,
            true,
          )
        }
      } catch (error) {
        appViewStore.setShowAlert(
          'error',
          t('failedToGeneratePrintouts', 'Failed to generate printous'),
        )
      } finally {
        printoutsViewStore.setLoading(false)
      }
    }

    const fetchLinkedOrderData = async (orderId: number) => {
      const order = _.values(orderItemStore.elements).filter(o => o.id === orderId)[0]
      const linkedOrderId = order && order.data?.linkedOrderId

      let linkedOrder: OrderResponseDto | null = null

      if (linkedOrderId) {
        linkedOrder = await orderService.getById(linkedOrderId)
      }

      return linkedOrder
    }

    async function generateDropoffEir(data: IGateInFormData, now: Date) {
      const { dropOffOrders } = store
      await generateEir(dropOffOrders, data, now)
    }

    async function generateRoutingSlip(data: IGateInFormData, now: Date) {
      const {
        pickUpOrders,
        dropOffOrders,
        nnrOrders,
        pickUpGeneralCargoOrders,
        dropOffGeneralCargoOrders,
      } = store
      const date = now.toLocaleDateString()

      const containerDropOffOrders = dropOffOrders.filter(o => o.containerNumber)
      const containerPickUpOrders = pickUpOrders.filter(o => o.containerNumber)
      const isGeneralCargoOrder = (order: IInspectContainerFormData) =>
        !order.containerNumber && order.commodityId !== null && order.commodityId !== undefined
      const generalCargoDropOffOrders = _.uniqBy(
        [
          ...dropOffGeneralCargoOrders,
          ...dropOffOrders.filter(isGeneralCargoOrder),
        ] as IInspectContainerFormData[],
        'id',
      )
      const generalCargoPickUpOrders = _.uniqBy(
        [
          ...pickUpGeneralCargoOrders,
          ...pickUpOrders.filter(isGeneralCargoOrder),
        ] as IInspectContainerFormData[],
        'id',
      )

      const dropOffRoutingInfo = await getJourneyPositions?.(
        containerDropOffOrders.filter(o => o.journeyId).map(o => o.journeyId!),
        true,
      )
      const pickUpRoutingInfo = await getJourneyPositions?.(
        containerPickUpOrders.filter(o => o.journeyId).map(o => o.journeyId!),
      )

      const mapGeneralCargoOrders = async (
        orders: IInspectContainerFormData[],
      ): Promise<RoutingSlipItem[]> => {
        const generalCargoRoutingInfo = getGeneralCargoRoutingInfo
          ? await getGeneralCargoRoutingInfo(orders)
          : []
        const locationsByOrderId = new Map<number, string>(
          generalCargoRoutingInfo.map(info => [info.orderId, info.location]),
        )

        return orders.map(order => ({
          type: 'generalCargo' as const,
          referenceNumber: order.referenceNumber ?? undefined,
          commodityName: order.commodityName ?? undefined,
          commodityAttributes: order.commodityAttributes ?? undefined,
          plannedCargoAmount: order.plannedCargoAmount ?? undefined,
          location:
            locationsByOrderId.get(order.id)?.trim() ||
            t('noLocationProvided', 'No location provided'),
        }))
      }

      const dropOffsWithRouteInfo: RoutingSlipItem[] = [
        ...containerDropOffOrders.map(c => {
          const containerRountingInfo = dropOffRoutingInfo?.find(r => r.journeyId === c.journeyId)

          return {
            type: 'numeric' as const,
            containerNumber: c.containerNumber!,
            location: containerRountingInfo?.position ?? 'No location provided',
          }
        }),
        ...(await mapGeneralCargoOrders(generalCargoDropOffOrders)),
      ]
      const pickUpOrdersWithRouteInfo: RoutingSlipItem[] = [
        ...containerPickUpOrders.map(c => {
          const containerRountingInfo = pickUpRoutingInfo?.find(r => r.journeyId === c.journeyId)

          return {
            type: 'numeric' as const,
            containerNumber: c.containerNumber!,
            location: containerRountingInfo?.position ?? 'No location provided',
          }
        }),
        ...(await mapGeneralCargoOrders(generalCargoPickUpOrders)),
      ]

      const nonNumericOrdersWithRouteInfo: RoutingSlipItem[] =
        await printoutsViewStore.mapNnrOrders(nnrOrders)

      const pickUpsWithRouteInfo = [...pickUpOrdersWithRouteInfo, ...nonNumericOrdersWithRouteInfo]

      if (dropOffsWithRouteInfo.length > 0 || pickUpsWithRouteInfo.length > 0) {
        const miniMap = renderRouteMiniMap ? await renderRouteMiniMap(data.truckVisitId) : null

        const document = (
          <RoutingSlip
            dropOffs={dropOffsWithRouteInfo}
            pickUps={pickUpsWithRouteInfo}
            miniMap={miniMap}
            t={t}
          />
        )

        await printoutsViewStore.generatePrintout('RoutingSlip', document, date)
      }
    }

    async function generateEir(
      orders: IInspectContainerFormData[],
      data: IGateInFormData,
      now: Date,
    ) {
      const date = now.toLocaleDateString()
      const containerOrders = orders.filter(o => o.containerNumber)

      for (const order of containerOrders) {
        const eirData = await generateEirDataForOrder(order, now, {
          truckPlate: data.truckPlate,
          truckCompanyName: store.truckCompany?.name,
          driverName: store.externalDriver?.name,
          driverID: store.externalDriver?.driverID,
          fetchLinkedOrderData,
          orderItemStore,
          vesselVisitItemStore,
        })

        const doc = <EquipmentInterchangeReceipt t={t} data={eirData} />

        await printoutsViewStore.generatePrintout(
          'Eir',
          doc,
          date,
          order.containerNumber ?? t('unknownContainer', 'Unknown container'),
          order.id,
        )
      }
    }

    const validateNoOrderVisit = () => {
      return (
        !!store.dropOffOrders.length ||
        !!store.pickUpOrders.length ||
        !!store.bookings.length ||
        !!store.nnrOrders.length ||
        !!store.pickUpGeneralCargoOrders.length ||
        !!store.dropOffGeneralCargoOrders.length
      )
    }

    const validateNnrOrdersWithoutPickUpAmount = () => {
      return store.nnrOrders.every(nnr => nnr.pickUpAmount)
    }

    const onEditDropOffOrder = (order: IInspectContainerFormData) => {
      store.toggleEditDialogVisibility(true)
      store.setEditedOrder(order)
      store.setEditedType('dropOff')
    }

    const onEditPickUpOrder = (order: IInspectContainerFormData) => {
      store.toggleEditDialogVisibility(true)
      store.setEditedOrder(order)
      store.setEditedType('pickUp')
    }

    const showNoOrdersHint = !!errors.dropOffOrders && !!errors.pickUpOrders
    const showSelectPickUpAmountAlert = !errors.dropOffOrders && !!errors.pickUpOrders

    const onTruckPlateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      e.target.value = escapeTruckNumber(e.target.value)

      store.setLicensePlate(e.target.value)
    }

    const onTrailerPlateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      e.target.value = escapeTruckNumber(e.target.value)

      store.setTrailerPlate(e.target.value)
    }

    return (
      <InitializationWrapper isInitializing={loading || store.loading}>
        <Box sx={{ backgroundColor: theme.palette.grey[100], minHeight: '100%' }}>
          <TruckAppointmentPageHeader
            viewStore={store}
            onSubmit={handleSubmit(onGateInFormSubmit)}
          />
          {/* [FF] TODO: remove DialogContent tag when ff is integrated */}
          <DialogContent>
            <Box minHeight={'450px'}>
              <NoOrdersHint show={showNoOrdersHint} />
              <SelectPickUpAmountAlert show={showSelectPickUpAmountAlert} />

              {/* APPOINTMENT DETAILS */}

              <Grid container spacing={2}>
                <Grid mt={2} mb={2} size={6}>
                  <Box
                    padding={2}
                    border={1}
                    borderColor='divider'
                    borderRadius={1}
                    bgcolor={'white'}
                  >
                    <Grid container spacing={2}>
                      <Grid size={12}>
                        <Typography variant='subtitle1'>
                          {t('truckDetails', 'Truck details')}
                        </Typography>
                      </Grid>

                      <Grid size={6}>
                        <TextField
                          data-cy='gate-in-truck-plate-input'
                          fullWidth
                          required
                          label={`${t('truckPlateNumber', 'Truck plate number')}`}
                          variant='outlined'
                          {...register('truckPlate', { required: true })}
                          name='truckPlate'
                          onChange={onTruckPlateChange}
                          error={!!errors.truckPlate}
                          helperText={
                            errors.truckPlate
                              ? t('truckPlateNumberMandatory', 'Truck plate number is mandatory')
                              : ''
                          }
                          slotProps={{ htmlInput: { maxLength: 20 } }}
                        />
                      </Grid>
                      {isTruckAppointment && (
                        <>
                          <Grid size={6}>
                            <TextField
                              data-cy='gate-in-trailer-plate-input'
                              fullWidth
                              label={`${t('trailerPlateNumber', 'Trailer plate number')}`}
                              variant='outlined'
                              {...register('trailerPlate')}
                              name='trailerPlate'
                              onChange={onTrailerPlateChange}
                              error={!!errors.trailerPlate}
                              slotProps={{ htmlInput: { maxLength: 20 } }}
                            />
                          </Grid>
                          <Grid size={6}>
                            <Controller
                              name='externalDriverId'
                              rules={{ required: true }}
                              control={control}
                              render={({ field }) => (
                                <DriverAutoComplete
                                  initialValueName='driverID'
                                  selectedDriver={store.externalDriver}
                                  label={t('driverIdNo', 'Driver ID no')}
                                  searchBy='driverID'
                                  required
                                  helperText={
                                    errors.externalDriverId
                                      ? t('fieldRequired', 'Field required')
                                      : ''
                                  }
                                  error={!!errors.externalDriverId}
                                  onChange={driver => {
                                    store.setExternalDriver(driver)
                                    field.onChange(driver?.id)
                                  }}
                                  dataCy='driver-id-autocomplete'
                                />
                              )}
                            />
                          </Grid>
                          <Grid size={6}>
                            <Controller
                              name='externalDriverId'
                              rules={{ required: true }}
                              control={control}
                              render={({ field }) => (
                                <DriverAutoComplete
                                  initialValueName='name'
                                  selectedDriver={store.externalDriver}
                                  label={t('driverName', 'Driver name')}
                                  searchBy='name'
                                  required
                                  helperText={
                                    errors.externalDriverId
                                      ? t('fieldRequired', 'Field required')
                                      : ''
                                  }
                                  error={!!errors.externalDriverId}
                                  onChange={driver => {
                                    store.setExternalDriver(driver)
                                    field.onChange(driver?.id)
                                  }}
                                />
                              )}
                            />
                          </Grid>
                          {store.externalDriver?.banned && (
                            <Grid size={12}>
                              <CustomAlert
                                message={t(
                                  'cateInIsBlockedDueToExternalDriverBanned',
                                  'Gate in is blocked due to the external driver being banned.',
                                )}
                                severity='warning'
                              />
                            </Grid>
                          )}
                          <Grid size={6}>
                            <Controller
                              name='truckCompanyId'
                              rules={{ required: true }}
                              control={control}
                              render={({ field: { onChange } }) => (
                                <CompanyAutocomplete
                                  label={t('transportCompany', 'Transport company')}
                                  required={true}
                                  selectedCustomer={store.truckCompany}
                                  handleOnChange={customer => {
                                    store.setTruckCompany(customer)
                                    onChange(customer?.id)
                                  }}
                                  error={!!errors.truckCompanyId}
                                  helperText={
                                    errors.truckCompanyId
                                      ? t('fieldIsRequired', 'Field is required.')
                                      : ''
                                  }
                                  filterByTypes={[CompanyType.TransportCompany]}
                                />
                              )}
                            />
                          </Grid>
                          <Grid size={6}>
                            <TextField
                              fullWidth
                              label={`${t('externalRefGatePassNo', 'External ref/Gate pass no.')}`}
                              {...register('externalPortGatePassRef', { required: false })}
                              variant='outlined'
                              name='externalPortGatePassRef'
                              onChange={e => store.setExternalPortGatePassRef(e.target.value)}
                              sx={{ mb: '1rem' }}
                              data-cy='external-ref-no'
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Box>
                </Grid>

                {isTruckAppointment && (
                  <Grid mt={2} mb={2} size={6}>
                    <Box
                      padding={2}
                      border={1}
                      borderColor='divider'
                      borderRadius={1}
                      bgcolor={'white'}
                      height={'100%'}
                    >
                      <Grid container spacing={2}>
                        <Grid size={12}>
                          <Typography variant='subtitle1'>
                            {t('arrivalWindow', 'Arrival window')}
                          </Typography>
                        </Grid>
                        <Grid size={6}>
                          <Controller
                            name='truckAppointmentDate'
                            rules={{ required: true }}
                            control={control}
                            render={() => (
                              <Box data-cy={'arrival-date-picker'}>
                                <CustomDatePicker
                                  label={
                                    <>
                                      {t('date', 'Date')} <span style={{ color: 'red' }}>*</span>
                                    </>
                                  }
                                  value={
                                    store.truckAppointmentDate
                                      ? dayjs(store.truckAppointmentDate)
                                      : undefined
                                  }
                                  onChange={data =>
                                    store.setTruckAppointmentDate(dayjs(data).toISOString())
                                  }
                                  error={!!errors.truckAppointmentDate}
                                  helperText={
                                    errors.truckAppointmentDate
                                      ? t(
                                          'arrivalWindowDateMandatory',
                                          'Arrival window date is mandatory',
                                        )
                                      : ''
                                  }
                                />
                              </Box>
                            )}
                          />
                        </Grid>
                        <Grid size={6} />
                        <Grid size={6}>
                          <CustomTimePicker
                            ampm={false}
                            fullWidth
                            label={t('from', 'From')}
                            value={
                              store.truckAppointmentStartTime
                                ? dayjs(store.truckAppointmentStartTime)
                                : null
                            }
                            autoFillMinutes
                            onChange={(data: string) => {
                              const truckArrivalWindowDuration =
                                tenantStore.tenant?.truckArrivalWindowDuration ?? 0
                              const time = dayjs(data)

                              const startTime = time.toISOString()
                              const endTime = time
                                .add(truckArrivalWindowDuration, 'hour')
                                .toISOString()

                              store.setTruckAppointmentStartTime(startTime)
                              store.setTruckAppointmentEndTime(endTime)
                            }}
                          />
                        </Grid>
                        <Grid size={6}>
                          <CustomTimePicker
                            ampm={false}
                            fullWidth
                            label={t('to', 'To')}
                            value={
                              store.truckAppointmentEndTime
                                ? dayjs(store.truckAppointmentEndTime)
                                : null
                            }
                            autoFillMinutes
                            onChange={(data: string) =>
                              store.setTruckAppointmentEndTime(dayjs(data).toISOString())
                            }
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                )}
              </Grid>

              {/* BOX */}
              <Grid container spacing={2}>
                <Grid size={6}>
                  <Controller
                    control={control}
                    name={`dropOffOrders`}
                    rules={{ validate: validateNoOrderVisit }}
                    render={() => {
                      return (
                        <OrderHandlingBox
                          isAllowedToCreateOrders={true}
                          orders={store.dropOffOrders}
                          bookings={store.bookings}
                          dropOffGeneralCargoOrders={dropOffGeneralCargoOrders}
                          type='DropOff'
                          error={!!errors.dropOffOrders}
                          onRemoveOrder={order => {
                            store.openRemoveDialog(order, 'DropOff')
                          }}
                          onRemoveGeneralCargoOrder={gco =>
                            store.deleteDropOffGeneralCargoOrder(gco.id)
                          }
                          onEditOrder={onEditDropOffOrder}
                          useSearchAutoComplete={true}
                          renderAutocomplete={() => <DropOffAutoComplete store={store} />}
                        />
                      )
                    }}
                  />
                </Grid>
                <Grid size={6}>
                  <Controller
                    control={control}
                    name={`pickUpOrders`}
                    rules={{
                      validate: () =>
                        validateNoOrderVisit() && validateNnrOrdersWithoutPickUpAmount(),
                    }}
                    render={() => {
                      return (
                        <OrderHandlingBox
                          isAllowedToCreateOrders={true}
                          orders={store.pickUpOrders}
                          nnrOrders={store.nnrOrders}
                          pickUpGeneralCargoOrders={pickUpGeneralCargoOrders}
                          type='PickUp'
                          error={!!errors.pickUpOrders}
                          useSearchAutoComplete
                          onRemoveOrder={order => {
                            store.openRemoveDialog(order, 'PickUp')
                          }}
                          onEditOrder={onEditPickUpOrder}
                          onEditNnrOrderAmount={store.upsertNNROrder}
                          onRemoveNNROrder={nnr => store.deleteNNROrder(nnr.id)}
                          onRemoveGeneralCargoOrder={gco =>
                            store.deletePickUpGeneralCargoOrder(gco.id)
                          }
                          renderAutocomplete={() => <PickUpAutoComplete store={store} />}
                          validateOutboundRequest={store.validateOutboundRequest}
                          isForAppointment={!tenantStore.skipTruckAppointment}
                        />
                      )
                    }}
                  />
                </Grid>
              </Grid>
              <GateInFormDialogs store={store} reset={reset} />
            </Box>
          </DialogContent>
          <ExternalDriverFormDialog />
        </Box>
      </InitializationWrapper>
    )
  },
)
